import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, Renderer2 } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { DEFAULT_DYNAMIC_TABLE, DEFAULT_EMPTY_TABLE, DEFAULT_STATIC_TABLE, DynamicTableConfig, DynamicTableData, DynamicTableDataPoint, StaticTableData, TableComponentData, TableDataPointType, TableDataType, TableDirection, TableEntry, TableGroup, TableHorizontalAlignment, TableVerticalAlignment } from "@core/data/types/Reports/report-tables";
import { Dialog, Snackbar } from "@core/material";
import { Select, Store } from "@ngxs/store";
import { Observable, Subject } from "rxjs";
import { InitReportTableForm, ReportTableFormState, ReportTableFormStateModel } from "./report-table-edit.form.state";
import { debounceTime, takeUntil } from "rxjs/operators";
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ChartContextType, Status } from "@core/data";
import { ReportDataContext } from "@core/data/types/advanced-report";
import { Calculation, Characteristic, Index, Library, Measurement, Property } from '@library';
import { LIBRARY } from '@app/advanced-report/library';
import { SizeClassification } from 'library/src/classifications/_types';

export interface ReportTableEditFormDialogData {
    reportKey: string;
    ownerOrgKey: string;
    optionKey: string;
    data: TableComponentData;
}

@Component({
    selector: 'pv-report-table-edit-form-dialog',
    templateUrl: 'report-table-edit.form.dialog.html',
    styleUrls:   ['report-table-edit.form.dialog.scss'],
    preserveWhitespaces: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'pv-report-table-edit-form-dialog',
    }
})
export class ReportTableEditFormDialog implements OnInit, OnDestroy {

    @Select(ReportTableFormState)
    state$: Observable<ReportTableFormStateModel>;

    entriesMax: number = 0;
    activeCell: string | null = null;

    currentVertical: TableVerticalAlignment = "middle";
    currentHorizontal: TableHorizontalAlignment = "left";

    lastGroupIndex = 0;
    lastEntryIndex = 0;

    //? DYNAMIC TABLE OPTION CONTROLS
    calculationsControl: FormControl = new FormControl([]);
    charsControl: FormControl = new FormControl([]);
    indexesControl: FormControl = new FormControl([]);
    measuresControl: FormControl = new FormControl([]);
    propertiesControl: FormControl = new FormControl([]);
    classificationsControl: FormControl = new FormControl([]);

    // TODO maybe switch this to the createTableForm logic and dynamically build the group
    formGroup: FormGroup;

    tableContextOptions = [
        {value: ChartContextType.CULTIVAR, label: 'Cultivar'},
        {value: ChartContextType.SITE, label: 'Site'},
        {value: ChartContextType.SAMPLE, label: 'Sample'},
        {value: ChartContextType.EVALUATION, label: 'Evaluation'},
    ];

    isPending: boolean = false;

    // Session-scoped memory of both variants' last-known settings, so toggling `type` back and forth
    // restores instead of destroying the variant being switched away from. `_activeType` tracks what
    // updateFormStructure() last built, since the `type` control's own value has already moved to the
    // new type by the time its valueChanges subscriber fires.
    private _activeType: TableDataType | null = null;
    private _stash: Partial<Record<TableDataType, TableComponentData>> = {};

    private _destroy$ = new Subject();
    private _dynamicFormDestroy$ = new Subject<void>();

    constructor(
        private _store: Store,
        private fb: FormBuilder,
        private _snackbar: Snackbar,
        private _dialogRef: MatDialogRef<ReportTableEditFormDialogData>,
        private _changes: ChangeDetectorRef,
        private _dialog: Dialog,
        private renderer: Renderer2,
        @Inject(LIBRARY) private _library: Library,
        @Inject(MAT_DIALOG_DATA) private _data: ReportTableEditFormDialogData,
    ) {}

    ngOnInit() {
        const { reportKey, ownerOrgKey, data, optionKey } = this._data;

        this._store.dispatch(new InitReportTableForm(reportKey, ownerOrgKey, optionKey, data));

        this.state$.pipe(takeUntil(this._destroy$))
            .subscribe((state: ReportTableFormStateModel) => {
                if (state.status == Status.OK) this.reset(state.data);

               if (state.status === Status.LOADING) {
                    this.isPending = true;
                    this._changes.detectChanges();

                    setTimeout(() => {
                        this.isPending = false;
                        this._changes.detectChanges();
                    }, 5000);
                }
            });

        this.calculationsControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((arr: Calculation[]) => {
                this.compileDataPoints(arr, TableDataPointType.CALCULATION);
            });

        this.charsControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((arr: Characteristic[]) => {
                this.compileDataPoints(arr, TableDataPointType.CHARACTERISTIC);
            });

        this.indexesControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((arr: Index[]) => {
                this.compileDataPoints(arr, TableDataPointType.INDEX);
            });

        this.measuresControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((arr: Measurement[]) => {
                this.compileDataPoints(arr, TableDataPointType.MEASUREMENT);
            });

        this.propertiesControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((arr: Property[]) => {
                this.compileDataPoints(arr, TableDataPointType.PROPERTY);
            });

        this.classificationsControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((arr: SizeClassification[]) => {
                this.compileDataPoints(arr, TableDataPointType.CLASSIFICATION);
            });
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    reset(data: TableComponentData) {
        // Seed the session stash from what was persisted: the active variant, and whatever the other
        // variant's settings were last saved as (if any — absent on tables saved before this existed).
        this._activeType = null;
        this._stash = {};
        // Strip any nested inactiveVariant before stashing — extractStaticData()/extractDynamicData()
        // never produce one, so this keeps both seeding paths symmetric even if a stray one reached the API.
        if (data?.type) {
            const { inactiveVariant, ...active } = data as any;
            this._stash[data.type] = active;
        }
        if (data?.inactiveVariant?.type) {
            const { inactiveVariant, ...inactive } = data.inactiveVariant as any;
            this._stash[data.inactiveVariant.type] = inactive;
        }

        this.formGroup = this.createBaseForm(data);

        this.formGroup.get('type').valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(newType => {
                this.updateFormStructure(newType, this.formGroup, data);
                this._changes.markForCheck();
            });
    }

    createBaseForm(data?: TableComponentData) {
        const form = this.fb.group({
            label: [data?.label || DEFAULT_EMPTY_TABLE.label],
            type: [data?.type || DEFAULT_EMPTY_TABLE.type, Validators.required],
            showBorders: [data?.showBorders ?? true],
        });

        if (data.type === TableDataType.STATIC && data.groups.length > 0) {
            this.currentVertical = data.groups[0].entries[0].verticalAlign;
            this.currentHorizontal = data.groups[0].entries[0].horizontalAlign;
            this.lastGroupIndex = data.groups.length - 1;
            this.lastEntryIndex = data.groups[0].entries.length - 1;
        }

        if (data.type === TableDataType.DYNAMIC) {
            let config: DynamicTableConfig = {
                ...DEFAULT_DYNAMIC_TABLE.config,
                ...data?.config,
            };

            this.currentVertical = config.verticalAlign;
            this.currentHorizontal = config.horizontalAlign;
        }

        this.updateFormStructure(form.get('type').value, form, data);

        return form;
    }

    updateFormStructure(type: string, form: FormGroup = this.formGroup, data?: any) {
        this._dynamicFormDestroy$.next();

        // Snapshot the outgoing variant's live values before its controls are destroyed below, so
        // toggling back later restores them instead of falling back to defaults or stale open-time data.
        if (this._activeType && this._activeType !== type) {
            this._stash[this._activeType] = this._activeType === TableDataType.STATIC
                ? this.extractStaticData(form.value, TableDataType.STATIC)
                : this.extractDynamicData(form.value, TableDataType.DYNAMIC);
        }

        // Prefer this session's own stash for the target type, then whichever of the dialog's two
        // open-time sources (the active variant, or its saved inactiveVariant) matches.
        const seed: any = this._stash[type as TableDataType]
            ?? (data?.type === type ? data : undefined)
            ?? (data?.inactiveVariant?.type === type ? data.inactiveVariant : undefined);

        const conditionalControls = [
            'groups',
            'direction',
            'context',
            'dataPoints',
            'config',
            'system',
        ];

        conditionalControls.forEach(ctrl => form.removeControl(ctrl));

        if (type === TableDataType.STATIC) {
            if (seed?.groups?.length && seed.groups[0]?.entries?.length) {
                this.currentVertical = seed.groups[0].entries[0].verticalAlign;
                this.currentHorizontal = seed.groups[0].entries[0].horizontalAlign;
            } else {
                this.currentVertical = "middle";
                this.currentHorizontal = "left";
            }

            form.addControl('groups', this.fb.array(
                seed?.groups?.length
                    ? seed.groups.map(group => this.createGroupForm(group.entries.length, group))
                    : DEFAULT_STATIC_TABLE.groups.map(group => this.createGroupForm(2, group))
            ));
        }

        // TODO ADD CONVERSIONS TO THE FORM
        if (type === TableDataType.DYNAMIC) {
            if (seed?.config) {
                this.currentVertical = seed.config.verticalAlign;
                this.currentHorizontal = seed.config.horizontalAlign;
            } else {
                this.currentVertical = "middle";
                this.currentHorizontal = "left";
            }

            let config: DynamicTableConfig = {
                ...DEFAULT_DYNAMIC_TABLE.config,
                ...seed?.config,
                verticalAlign: this.currentVertical,
                horizontalAlign: this.currentHorizontal,
            };

            const contextControl = this.fb.control(seed?.context || DEFAULT_DYNAMIC_TABLE.context, [Validators.required]);

            form.addControl('direction', this.fb.control(seed?.direction || DEFAULT_DYNAMIC_TABLE.direction, Validators.required));
            form.addControl('context', contextControl);
            form.addControl('dataPoints', this.fb.array(seed?.dataPoints || DEFAULT_DYNAMIC_TABLE.dataPoints, [Validators.required, Validators.maxLength(12)]));
            form.addControl('config', this.fb.control(config, [Validators.required]));
            form.addControl('system', this.fb.control(seed?.system || DEFAULT_DYNAMIC_TABLE.system, Validators.required));

            // Clear before reseeding — resetOptionControls() only adds, so a picklist holding entries
            // from a previously active dynamic variant would otherwise keep them alongside this one's.
            this.calculationsControl.patchValue([], { emitEvent: false });
            this.charsControl.patchValue([], { emitEvent: false });
            this.indexesControl.patchValue([], { emitEvent: false });
            this.measuresControl.patchValue([], { emitEvent: false });
            this.propertiesControl.patchValue([], { emitEvent: false });
            this.classificationsControl.patchValue([], { emitEvent: false });

            this.resetOptionControls(seed?.dataPoints);

            contextControl.valueChanges
                .pipe(takeUntil(this._dynamicFormDestroy$), debounceTime(100))
                .subscribe(newContext => {
                    this.propertiesControl.patchValue([]);
                    this._snackbar.info('Context Updated');
                });
        }

        form.updateValueAndValidity();

        this._activeType = type as TableDataType;
    }

    extractStaticData(formValue: any, type: TableDataType.STATIC): StaticTableData {
        const { label, groups, showBorders } = formValue;
        return { label, type, groups, showBorders };
    }

    extractDynamicData(formValue: any, type: TableDataType.DYNAMIC): DynamicTableData {
        const { label, context, direction, dataPoints, config, system, showBorders } = formValue;
        return { label, type, context, direction, dataPoints, config, system, showBorders };
    }

    resetOptionControls(dataPoints: DynamicTableDataPoint[]) {
        if (!dataPoints) return;

        dataPoints.forEach(dataPoint => {
            let values;
            if (dataPoint.type === TableDataPointType.CALCULATION) {
                let calculation = this._library.calculations.get(dataPoint.id);
                values = this.calculationsControl.value;
                this.calculationsControl.patchValue([...new Set([...values, calculation])].filter(Boolean), { emitEvent: false });
            }

            if (dataPoint.type === TableDataPointType.CHARACTERISTIC) {
                let characteristic = this._library.chars.get(dataPoint.id);
                values = this.charsControl.value;
                this.charsControl.patchValue([...new Set([...values, characteristic])].filter(Boolean), { emitEvent: false });
            }

            if (dataPoint.type === TableDataPointType.INDEX) {
                let index = this._library.indexes.get(dataPoint.id);
                values = this.indexesControl.value;
                this.indexesControl.patchValue([...new Set([...values, index])].filter(Boolean), { emitEvent: false });
            }

            if (dataPoint.type === TableDataPointType.MEASUREMENT) {
                let measure = this._library.measures.get(dataPoint.id);
                values = this.measuresControl.value;
                this.measuresControl.patchValue([...new Set([...values, measure])].filter(Boolean), { emitEvent: false });
            }

            if (dataPoint.type === TableDataPointType.PROPERTY) {
                let property = this._library.properties.get(dataPoint.id);
                values = this.propertiesControl.value;
                this.propertiesControl.patchValue([...new Set([...values, property])].filter(Boolean), { emitEvent: false });
            }

            if (dataPoint.type === TableDataPointType.CLASSIFICATION) {
                let property = this._library.classifications.get(dataPoint.id);
                values = this.classificationsControl.value;
                this.classificationsControl.patchValue([...new Set([...values, property])].filter(Boolean), { emitEvent: false });
            }
        });
    }

    createGroupForm(totalEntries: number, group?: TableGroup) {
        let defaults = [];

        for (let i = 0; i < totalEntries; i++) {
            defaults.push(this.createEntryForm());
        }

        let groupForm = this.fb.group({
            entries: this.fb.array(
                group?.entries?.length ? group.entries.map(entry => this.createEntryForm(entry)) : defaults
            )
        });

        let entries = groupForm.value.entries.length;

        this.entriesMax = entries > this.entriesMax ? entries : this.entriesMax;

        return groupForm;
    }

    createEntryForm(entry?: TableEntry) {
        return this.fb.group({
            colSpan: [entry?.colSpan || 1, [Validators.required, Validators.min(1)]],
            rowSpan: [entry?.rowSpan || 1, [Validators.required, Validators.min(1)]],
            content: [entry?.content || ''],
            verticalAlign: [this.currentVertical],
            horizontalAlign: [this.currentHorizontal],
            fill: [entry?.fill || '#fff'],
        });
    }


    get groupsFormArray(): FormArray {
        return this.formGroup.get('groups') as FormArray | null;
    }

    get dataPointsFormArray(): FormArray {
        return this.formGroup.get('dataPoints') as FormArray;
    }

    get dataPointControls(): AbstractControl[] {
        return this.dataPointsFormArray.controls;
    }

    getEntriesFormArray(groupIndex: number): FormArray {
        if (!this.groupsFormArray) return null;

        const groupForm = this.groupsFormArray.at(groupIndex) as FormGroup;
        return groupForm.get('entries') as FormArray;
    }

    // In standard tables these are rows
    addTableGroup() {
        if (!this.groupsFormArray) return null;

        let size = this.groupsFormArray.value[0]?.entries?.length || 0;
        this.groupsFormArray.push(this.createGroupForm(size));
        this._changes.markForCheck();
    }

    removeTableGroup(index: number, isLast: boolean = false) {
        if (!this.groupsFormArray) return null;

        this.groupsFormArray.removeAt(index);
        if (isLast) this.lastGroupIndex -= 1;
        this._changes.markForCheck;
    }

    // In standard tables these are columns
    addTableEntry(groupIndex: number) {
        if (!this.groupsFormArray) return null;

        this.groupsFormArray.value.forEach((data, index) => {
            let entries = this.getEntriesFormArray(index);
            entries.push(this.createEntryForm());
        })

        this._changes.markForCheck();
    }

    removeTableEntry(entryIndex: number, isLast: boolean = false) {
        if (!this.groupsFormArray) return null;

        this.groupsFormArray.value.forEach((data, index) => {
            let entries = this.getEntriesFormArray(index);
            entries.removeAt(entryIndex);
        })

        if (isLast) this.lastEntryIndex -= 1;

        this._changes.markForCheck();
    }

    setHorizontial(position: "left" | "center" | "right") {
        if (this.formGroup.get('type')?.value === TableDataType.STATIC) {
            this.groupsFormArray.value.forEach(group => {
                group.entries.forEach((entry: TableEntry) => {
                    entry.horizontalAlign = position
                });
            })
        }

        if (this.formGroup.get('type')?.value === TableDataType.DYNAMIC) {
            let config: DynamicTableConfig = {
                ...this.formGroup.get('config').value,
                horizontalAlign: position,
            };

            this.formGroup.get('config').patchValue(config);
        }

        this.currentHorizontal = position;
        this._changes.markForCheck();
    }

    setVertical(position: "top" | "middle" | "bottom") {
        if (this.formGroup.get('type')?.value === TableDataType.STATIC) {
            this.groupsFormArray.value.forEach(group => {
                group.entries.forEach((entry: TableEntry) => {
                    entry.verticalAlign = position;
                });
            })
        }

        if (this.formGroup.get('type')?.value === TableDataType.DYNAMIC) {
            let config: DynamicTableConfig = {
                ...this.formGroup.get('config').value,
                verticalAlign: position,
            };

            this.formGroup.get('config').patchValue(config);
        }


        this.currentVertical = position;
        this._changes.markForCheck();
    }

    getOrientation(direction: string, orientationId: string) {
        if (this.formGroup.get('type')?.value === TableDataType.STATIC) {
            const entries = this.getEntriesFormArray(0);
            if (!entries || entries.length <= 0) return;

            const values = entries.value
            if (!values || values.length <= 0) return;

            if (direction == 'vertical') return this.currentVertical === orientationId;
            if (direction == 'horizontal') return this.currentHorizontal === orientationId;
        }

        if (this.formGroup.get('type')?.value === TableDataType.DYNAMIC) {
            let config: DynamicTableConfig = this.formGroup.get('config').value;
            if (direction == 'vertical') return config.verticalAlign === orientationId;
            if (direction == 'horizontal') return config.horizontalAlign === orientationId;
        }

        return false;
    }

    compileDataPoints(values: any[], type: TableDataPointType) {
        let formData: DynamicTableDataPoint[] = this.formGroup.get('dataPoints').value;
        let newData = values.filter(val => !formData.map(data=> data.id).includes(val?.id));

        let deletedDataPoints: DynamicTableDataPoint[] = formData.filter(data => data.type === type).filter(data => !values.map(val => val?.id).includes(data.id));

        newData.forEach(value => {
            this.addDataPointGroup(value?.id, type);
        });

        deletedDataPoints.forEach(dataPoint => {
            let data: DynamicTableDataPoint[] = this.formGroup.get('dataPoints').value;
            let index = data.findIndex(val => val.id === dataPoint.id);

            if (index < 0) return;

            let formArray = this.formGroup.get('dataPoints') as FormArray;
            formArray.removeAt(index);
        });
    }

    addDataPointGroup(id: string, type: TableDataPointType) {
        let group = this.fb.group({
            id: id,
            type: type,
        })

        let array = this.formGroup.get('dataPoints') as FormArray;

        array.push(group);
    }

    removeDataPoint(index: number, type: TableDataPointType) {
        let formArray = this.formGroup.get('dataPoints') as FormArray;
        let dataPoint = formArray.value[index];

        if (type === TableDataPointType.CALCULATION) this.removeOptionWithDataPoint(this.calculationsControl, dataPoint);
        if (type === TableDataPointType.CHARACTERISTIC) this.removeOptionWithDataPoint(this.charsControl, dataPoint);
        if (type === TableDataPointType.INDEX) this.removeOptionWithDataPoint(this.indexesControl, dataPoint);
        if (type === TableDataPointType.MEASUREMENT) this.removeOptionWithDataPoint(this.measuresControl, dataPoint);
        if (type === TableDataPointType.PROPERTY) this.removeOptionWithDataPoint(this.propertiesControl, dataPoint);

        formArray.removeAt(index);
    }

    removeOptionWithDataPoint(control: FormControl, dataPoint: DynamicTableDataPoint) {
        let values = control.value.filter(data => data && data.id !== dataPoint.id);
        control.patchValue(values, { emitEvent: false });
    }

    attempt() {
        this.formGroup.updateValueAndValidity();
        this.formGroup.markAsDirty();
        this.formGroup.markAllAsTouched();

        if (this.formGroup.get('type')?.value === TableDataType.DYNAMIC && !this.formGroup.get('dataPoints').valid) return this._snackbar.error('Your Table requires at least one data point and a max of 12');
        if (!this.formGroup.valid) return this._snackbar.error('Invalid data, check your inputs and try again.');

        this.setHorizontial(this.currentHorizontal);
        this.setVertical(this.currentVertical);

        if (this.formGroup.get('type')?.value === TableDataType.STATIC) {
            const data: StaticTableData = this.extractStaticData(this.formGroup.value, TableDataType.STATIC);

            this._dialogRef.close({
                status: true,
                data: { ...data, inactiveVariant: this._stash[TableDataType.DYNAMIC] },
            });
        }

        if (this.formGroup.get('type')?.value === TableDataType.DYNAMIC) {
            const data: DynamicTableData = this.extractDynamicData(this.formGroup.value, TableDataType.DYNAMIC);

            this._dialogRef.close({
                status: true,
                data: { ...data, inactiveVariant: this._stash[TableDataType.STATIC] },
            });
        }
    }

    cancel() {
        this._dialogRef.close({status: false});
    }

    removeRow() {
        let groups = this.groupsFormArray;
        let isLast = false;

        if (groups.length - 1 <= this.lastGroupIndex){
            this.lastGroupIndex = groups.length - 1;
            isLast = true;
        }

        if (isLast && this.lastGroupIndex == 1) return this._snackbar.error('The minimum number of rows is 2');

        let row = document.querySelectorAll(`.row-${this.lastGroupIndex}`);

        row.forEach(el => {
            this.renderer.addClass(el, 'remove');
        });

        this._dialog.confirm(
            `Remove Row ${this.lastGroupIndex + 1}?`,
            `Are you sure you want to delete row ${this.lastGroupIndex + 1}?`,
            'Remove',
            'Cancel',
            true
        )
        .afterClosed()
        .subscribe(result => {
            row.forEach(el => {
                this.renderer.removeClass(el, 'remove');
            });

            if (!result) return;

            this.removeTableGroup(this.lastGroupIndex, isLast);
        });
    }

    removeColumn() {
        let groups = this.groupsFormArray.value;
        let isLast = false;

        if (groups[0].entries.length - 1 <= this.lastEntryIndex) {
            this.lastEntryIndex = groups[0].entries.length - 1;
            isLast = true;
        }

        if (isLast && this.lastEntryIndex == 1) return this._snackbar.error('The minimum number of columns is 2');

        let col = document.querySelectorAll(`.col-${this.lastEntryIndex}`);

        col.forEach(el => {
            this.renderer.addClass(el, 'remove');
        });

        this._dialog.confirm(
            `Remove Column ${this.lastEntryIndex + 1}?`,
            `Are you sure you want to delete column ${this.lastEntryIndex + 1}?`,
            'Remove',
            'Cancel',
            true
        )
        .afterClosed()
        .subscribe(result => {
            col.forEach(el => {
                this.renderer.removeClass(el, 'remove');
            });

            if (!result) return;

            this.removeTableEntry(this.lastEntryIndex, isLast);
        });
    }

    stopEditing(groupIndex: number, entryIndex: number) {
        this.lastGroupIndex = groupIndex;
        this.lastEntryIndex = entryIndex;

        this._changes.markForCheck();
    }

    // TODO check performance
    getColumnLabel(index: number): string {
        let label = '';
        while (index >= 0) {
            label = String.fromCharCode((index % 26) + 65) + label;
            index = Math.floor(index / 26) - 1;
        }
        return label;
    }
}